import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Logo from './Logo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout, isAuthenticated, userType } = useAuth();
  const { cartCount } = useCart();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = isAuthenticated
    ? [
        { name: 'Home', path: '/' },
        { name: 'Hub', path: '/hub' },
        { name: 'Shop', path: '/shop' },
        { name: 'Contact', path: '/contact' },
      ]
    : [
        { name: 'Home', path: '/' },
        { name: 'Locations', path: '/locations' },
        { name: 'Membership', path: '/membership' },
        { name: 'Day Pass', path: '/day-pass' },
        { name: 'Shop', path: '/shop' },
        { name: 'Book Tour', path: '/book-tour' },
        { name: 'Contact', path: '/contact' },
      ];

  return (
    <>
      <nav data-testid="navbar" className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-uf-black/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <Logo size="md" />
            </Link>

            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map(link => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  className="text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-uf-red transition-colors"
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/cart" className="relative text-white/70 hover:text-uf-red transition-colors" data-testid="nav-cart">
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-uf-red text-white text-xs w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              {isAuthenticated ? (
                <div className="flex items-center gap-4">
                  {userType === 'staff' && (
                    <Link to="/admin" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-uf-red transition-colors" data-testid="nav-admin-dashboard">
                      <LayoutDashboard size={16} />
                      Admin
                    </Link>
                  )}
                  <Link to="/hub" className="text-xs font-bold uppercase tracking-[0.15em] text-uf-red" data-testid="nav-hub">
                    Hub
                  </Link>
                  <button onClick={logout} className="text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-white" data-testid="logout-btn">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-uf-red transition-colors" data-testid="nav-login">
                    Log In
                  </Link>
                  <Link to="/membership" className="btn-primary" data-testid="nav-join">
                    <span>Join Now</span>
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 lg:hidden">
              <Link to="/cart" className="relative text-white" data-testid="mobile-cart">
                <ShoppingBag size={24} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-uf-red text-white text-xs w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button className="text-white" onClick={() => setIsOpen(true)} data-testid="mobile-menu-btn">
                <Menu size={28} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <button className="absolute top-6 right-6 text-white" onClick={() => setIsOpen(false)} data-testid="close-menu-btn">
              <X size={32} />
            </button>
            <div className="flex flex-col items-center gap-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: i * 0.1 } }}
                >
                  <Link 
                    to={link.path}
                    className="font-heading text-3xl font-bold text-white hover:text-uf-red transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.6 } }}>
                {isAuthenticated ? (
                  <div className="flex flex-col items-center gap-4 mt-4">
                    {userType === 'staff' && (
                      <Link to="/admin" className="flex items-center gap-2 font-heading text-2xl font-bold text-white hover:text-uf-red transition-colors" onClick={() => setIsOpen(false)} data-testid="mobile-nav-admin">
                        <LayoutDashboard size={22} />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link to="/hub" className="btn-primary" onClick={() => setIsOpen(false)}>
                      <span>Member Hub</span>
                    </Link>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 mt-4">
                    <Link to="/login" className="font-heading text-2xl font-bold text-white hover:text-uf-red transition-colors" onClick={() => setIsOpen(false)}>
                      Log In
                    </Link>
                    <Link to="/membership" className="btn-primary" onClick={() => setIsOpen(false)}>
                      <span>Join Now</span>
                    </Link>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
